import { Button, DropdownGroup, DropdownMenu } from '@toggle/design-system';
import { Alert } from '@toggle/toggle';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  usePostRead,
  usePostSubscription,
} from '~/react-query-hooks/events/use-event-alerts/useEventAlerts';

import * as S from './AlertsCard.styles';
import { AlertsAlfredCard } from './components/alerts-alfred-card/AlertsAlfredCard';
import { AlertsInsightCard } from './components/alerts-insight-card/AlertsInsightCard';

export interface AlertsCardProps {
  alertEvent: Alert;
}

export const AlertsCard = ({ alertEvent }: AlertsCardProps) => {
  const { t } = useTranslation('alerts');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const { mutate: markAsRead } = usePostRead();
  const { mutate: toggleTracking } = usePostSubscription();
  const {
    read,
    source_type: sourceType,
    tracking_status: trackingStatus,
  } = alertEvent;
  const isTrackingActive = trackingStatus === 'active';

  const dropdownItems = [
    [
      {
        label: read ? t('alerts:markAsUnread') : t('alerts:markAsRead'),
        key: 'read',
        onClick: () => markAsRead([alertEvent.id]),
      },
      ...(trackingStatus && trackingStatus !== 'completed'
        ? [
            {
              label: isTrackingActive
                ? t('alerts:stopTracking')
                : t('alerts:resumeTracking'),
              key: 'tracking',
              onClick: () =>
                toggleTracking({
                  source_id: alertEvent.source_id,
                  source_type: alertEvent.source_type,
                  track: !isTrackingActive,
                }),
            },
          ]
        : []),
    ],
  ];

  const dropdown = (
    <DropdownMenu
      triggerNode={<Button variant="empty" size="xsmall" iconName="Ellipsis" />}
      align="end"
      onOpenChange={setIsDropdownOpen}
      contentProps={{
        style: {
          borderRadius: '8px',
          minWidth: 'unset',
          width: 'fit-content',
          backgroundColor: 'var(--background-soft)',
        },
      }}
    >
      {dropdownItems.map((group, key) => (
        <DropdownGroup items={group} key={key} />
      ))}
    </DropdownMenu>
  );

  const isAlfredCard = sourceType === 'alfred';
  return (
    <S.Root
      data-testid="alerts-card"
      $read={read}
      $isDropdownOpen={isDropdownOpen}
    >
      <S.ContentWrapper>
        {isAlfredCard ? (
          <AlertsAlfredCard alertEvent={alertEvent} dropdown={dropdown} />
        ) : (
          <AlertsInsightCard alertEvent={alertEvent} dropdown={dropdown} />
        )}
      </S.ContentWrapper>
    </S.Root>
  );
};
